import { Link, createFileRoute, useRouter } from "@tanstack/react-router";
import { CloudUploadIcon, FileIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { buttonVariants } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  useUserPlan,
  userPlanQueryOptions,
} from "@/lib/queries/payment/userPlan";

export const Route = createFileRoute("/_app/document-redactor/")({
  component: RouteComponent,
  loader({ context: { queryClient } }) {
    queryClient.ensureQueryData(userPlanQueryOptions);
  },
});

function RouteComponent() {
  const [file, setFile] = useState<File | null>(null);

  const plan = useUserPlan();

  const router = useRouter();
  const { getRootProps, getInputProps, isDragActive, inputRef } = useDropzone({
    onDrop(acceptedFiles, fileRejections) {
      if (import.meta.env.DEV) console.error({ fileRejections, acceptedFiles });

      // preload even before you decide to hover the next button
      router.preloadRoute({ from: Route.fullPath, to: "./options" });

      if (acceptedFiles.length > 0) setFile(acceptedFiles[0]);
    },
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        [".pptx"],
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/tiff": [".tif", ".tiff"],
      "image/bmp": [".bmp"],
      "image/webp": [".webp"],
    },
    multiple: false,
  });

  return (
    <div className="px-4 py-6">
      <div className="mb-8 space-y-2">
        <h1 className="text-2xl font-bold tracking-tight">Document Redactor</h1>
        <p className="text-muted-foreground">
          Masks/Highlightes PIIs on your files
        </p>
      </div>

      <Card className="bg-gray-100 p-8 dark:bg-black">
        <div className="mb-8 text-center">
          <h2 className="text-xl font-semibold">
            Upload your document to start redacting
          </h2>
        </div>
        {file ? (
          <div className="flex justify-center">
            <div className="flex max-w-xl items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-black">
              <div className="flex items-center gap-4">
                <FileIcon />
                <div>
                  <p className="ark:text-white font-medium text-gray-700">
                    {file.name}
                  </p>
                  {/* <p className="text-sm text-gray-500">{fileSize}</p> */}
                </div>
              </div>
              <button
                onClick={() => setFile(null)}
                className={`flex h-5 w-5 items-center justify-center rounded-sm bg-gray-700`}
                aria-label="Remove file"
              >
                <XIcon size={18} className="text-white" />
              </button>
            </div>
          </div>
        ) : (
          <div
            {...getRootProps({
              "data-drag-active": isDragActive ? "true" : undefined,
              className:
                "flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-gray-200 bg-white p-12 data-[drag-active]:border-primary dark:border-gray-800 dark:bg-black",
            })}
          >
            <div className="mb-4 rounded-full p-4">
              <CloudUploadIcon className="h-6 w-6 text-gray-500" />
            </div>

            <div className="space-y-2 text-center">
              <p>
                <button className="cursor-pointer">
                  <span className="font-medium text-primary dark:text-gray-500">
                    Click to upload your file
                  </span>
                </button>
              </p>
              <p className="text-sm text-muted-foreground">or drag and drop</p>
              <input
                {...getInputProps({
                  ref: inputRef,
                  type: "file",
                  id: "fileInput",
                  className: "hidden",
                })}
              />
            </div>
          </div>
        )}
      </Card>

      <div className="mt-8 flex justify-center">
        {plan.isSuccess &&
        plan.data.info?.plan.features.available_services.includes(
          "FILE_REDACT",
        ) ? (
          <Link
            className={cn(
              buttonVariants(),
              "aria-disabled:cursor-not-allowed aria-disabled:opacity-50",
              "bg-gray-700 px-8 hover:bg-gray-600",
            )}
            disabled={!file}
            from={Route.fullPath}
            to="./options"
            mask={{
              to: ".",
            }}
            state={{
              documentRedactFile: file!,
            }}
          >
            Next
          </Link>
        ) : (
          <UpgradePlanModalButton />
        )}
      </div>
    </div>
  );
}
function UpgradePlanModalButton() {
  return (
    <AlertDialog>
      <AlertDialogTrigger
        className={cn(
          buttonVariants(),
          "aria-disabled:cursor-not-allowed aria-disabled:opacity-50",
          "bg-gray-700 px-8 hover:bg-gray-600",
        )}
      >
        <img src="/icons/crown-icon.svg" />
        Upgrade to use
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            This feature is unavailable with your plan
          </AlertDialogTitle>
          <AlertDialogDescription>
            Please upgrade your plan to access this feature
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction asChild>
            <Link to="/pricing">
              <img src="/icons/crown-icon.svg" />
              Upgrade
            </Link>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
